/**
 * BYO Studio — draft state for the converged top-stepper shell.
 *
 * Owns the single BYO draft (source input / code / packages / runtime vars /
 * destination output). Persistence is NOT done here — the live create/edit
 * paths (ByoSourceConfigureStep.tsx, ByoDestinationConfigureStep.tsx,
 * useByoEditSource.ts) each build + save independently. This hook used to
 * also own a `createSourceOnly`/`createFlow` persistence path of its own, but
 * it was never wired into either shell and had drifted from the live paths
 * (missing fixes those two later received) — removed as dead code,
 * 2026-08-20. Its one contribution the live paths lacked (rolling back an
 * orphaned code container when the follow-up source save fails) was ported
 * into useByoEditSource.ts's save() first.
 *
 * Runtime variables (KEY / TYPE / VALUE) are serialized into the code
 * container's `extra_data` string. The source/folder pickers and TestPanel are
 * self-wired to the probe tree, so this hook does NOT manage file listing.
 */

import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getAccessToken } from 'js/reducers/session/selectors';
import {
  BYO_PERF_DEFAULTS,
  createEmptyByoDraft,
  type BYOPerformanceConfig,
  type ByoCodeMode,
  type RuntimeVar,
} from 'new/components/flows/types/byo/core/model';
import type { ByoInputValue, ByoOutputValue } from 'new/components/flows/types/byo/v2/types';

// Runtime-variable domain rules live in core/model.ts (shared by the v2
// modals and both shells); re-exported here for existing studio imports.
export {
  isRuntimeVarInvalid,
  runtimeVarKeyError,
  serializeRuntimeVars,
  deserializeRuntimeVars,
} from 'new/components/flows/types/byo/core/model';
export type { RuntimeVar, RuntimeVarType } from 'new/components/flows/types/byo/core/model';

export interface UseStudioDraft {
  name: string;
  setName: (name: string) => void;
  description: string;
  setDescription: (description: string) => void;

  input: ByoInputValue;
  setInput: (next: ByoInputValue) => void;

  code: string;
  setCode: (code: string) => void;
  codeMode: ByoCodeMode;
  setCodeMode: (mode: ByoCodeMode) => void;
  predefinedId: number | string | null;
  setPredefinedId: (id: number | string | null) => void;
  packages: string[];
  setPackages: (next: string[]) => void;
  runtimeVars: RuntimeVar[];
  setRuntimeVars: (next: RuntimeVar[]) => void;
  performanceConfig: BYOPerformanceConfig;
  setPerformanceConfig: (next: BYOPerformanceConfig) => void;

  output: ByoOutputValue;
  setOutput: (next: ByoOutputValue) => void;

  token: string;
  sourceId: number | string | null;
  setSourceId: (id: number | string) => void;
}

export function useStudioDraft(): UseStudioDraft {
  const token = useSelector(getAccessToken) ?? '';
  const initial = useMemo(() => createEmptyByoDraft(), []);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [input, setInput] = useState<ByoInputValue>({
    connectionType: null,
    credentialId: null,
    folderPath: null,
    selectedFile: null,
    fileType: null,
  });
  const [code, setCode] = useState(initial.code.code);
  const [codeMode, setCodeMode] = useState<ByoCodeMode>(initial.code.mode);
  const [predefinedId, setPredefinedId] = useState<number | string | null>(null);
  const [packages, setPackages] = useState<string[]>(initial.code.packages);
  const [runtimeVars, setRuntimeVars] = useState<RuntimeVar[]>([]);
  const [performanceConfig, setPerformanceConfig] =
    useState<BYOPerformanceConfig>(BYO_PERF_DEFAULTS);
  const [output, setOutput] = useState<ByoOutputValue>({
    connectionType: null,
    credentialId: null,
    target: null,
    prependInputFilename: false,
    prependInputDir: false,
  });

  const [sourceId, setSourceId] = useState<number | string | null>(null);

  return {
    name,
    setName,
    description,
    setDescription,
    input,
    setInput,
    code,
    setCode,
    codeMode,
    setCodeMode,
    predefinedId,
    setPredefinedId,
    packages,
    setPackages,
    runtimeVars,
    setRuntimeVars,
    performanceConfig,
    setPerformanceConfig,
    output,
    setOutput,
    token,
    sourceId,
    setSourceId,
  };
}

export default useStudioDraft;
